import { fetchApi } from "./api";

export type GalleryCollection = "gallery-video" | "gallery-photo" | "beauty-episode";

export type GalleryItemInput = {
  collection: GalleryCollection;
  title?: string | null;
  description?: string | null;
  permalink?: string | null;
  video_path?: string | null;
  poster_path?: string | null;
  captions_path?: string | null;
  /** One expected name/term per line; absent = keep stored, null or "" = clear. */
  captions_terms?: string | null;
  alt_text?: string | null;
  duration_seconds?: number | null;
  published_at?: string | null;
  display_order?: number;
  is_active?: boolean;
};

export type CaptionsStatus = "none" | "queued" | "running" | "done" | "failed";

/** Starts a fresh Transcribe job (new job name + new .vtt key every run) and marks the row running. */
export const generateCaptions = (opts: { data: { id: string } }) =>
  fetchApi<{
    started: boolean;
    jobName: string;
    /** Custom vocabulary applied to this job, or null when it was not READY. */
    vocabulary: string | null;
  }>("media", "generateCaptions", opts.data);

/** Polls the row's job; on COMPLETED the row is already repointed at the new file. */
export const checkCaptions = (opts: { data: { id: string } }) =>
  fetchApi<{
    /** Transcribe's state: QUEUED | IN_PROGRESS | COMPLETED | FAILED. */
    status: string;
    captions_status?: CaptionsStatus;
    captions_path?: string;
    reason?: string;
    jobName?: string;
  }>("media", "checkCaptions", opts.data);

/**
 * Queues jobs for videos with no captions (status none/failed). Bulk and it
 * costs Transcribe minutes — loop on `remaining` only with the owner's go-ahead.
 */
export const generateMissingCaptions = (opts: {
  data: { collection?: GalleryCollection; limit?: number };
}) =>
  fetchApi<{
    queued: number;
    ids: string[];
    errors: Array<{ id: string; reason: string }>;
    remaining: number;
    /** Custom vocabulary applied to this batch, or null when it was not READY. */
    vocabulary: string | null;
    vocabularyState: string;
  }>("media", "generateMissingCaptions", opts.data);

/**
 * Transcribe custom vocabulary ("miinews-ax-terms") state. MISSING = never
 * created; PENDING = building; READY = used by new jobs; FAILED = rejected.
 */
export type CaptionsVocabularyState =
  | "READY"
  | "PENDING"
  | "FAILED"
  | "MISSING"
  | "UNKNOWN";

export type CaptionsVocabulary = {
  name: string;
  state: CaptionsVocabularyState;
  phraseCount: number;
  lastModified: string | null;
  /** Why the vocabulary is not usable, when it is not READY. */
  reason?: string | null;
};

/**
 * Creates the caption vocabulary if absent, updates it when the deployed
 * phrase list differs, and reports "unchanged" otherwise. Idempotent and free
 * — Transcribe does not bill for vocabularies. A new or updated vocabulary
 * goes PENDING for a minute or two and only affects jobs started after it is
 * READY; existing captions are untouched.
 */
export const ensureCaptionsVocabulary = (_opts?: { data?: Record<string, never> }) =>
  fetchApi<CaptionsVocabulary & { action: "created" | "updated" | "unchanged" }>(
    "media",
    "ensureCaptionsVocabulary",
    {},
  );

/** Read-only: current VocabularyState and LastModifiedTime. */
export const getCaptionsVocabulary = (_opts?: { data?: Record<string, never> }) =>
  fetchApi<CaptionsVocabulary>("media", "getCaptionsVocabulary", {});

/** Public listing: active rows only, already ordered by the API. */
export const listGalleryItems = (opts: { data: { collection: GalleryCollection } }) =>
  fetchApi<any[]>("content", "listGalleryItems", opts.data, { auth: false });

/** Admin listing: every row, active or not. Omit `collection` for all collections. */
export const listGalleryItemsAdmin = (opts: { data: { collection?: GalleryCollection } }) =>
  fetchApi<any[]>("content", "listGalleryItemsAdmin", opts.data);

export const createGalleryItem = (opts: { data: GalleryItemInput }) =>
  fetchApi<any>("content", "createGalleryItem", opts.data);

export const updateGalleryItem = (opts: { data: GalleryItemInput & { id: string } }) =>
  fetchApi<any>("content", "updateGalleryItem", opts.data);

/**
 * Writes captions_terms alone (the "Expected names and terms" dialog runs
 * from the table too, where there is no form to send). updateGalleryItem
 * needs collection/title/permalink and writes every unguarded column, so a
 * bare {id, captions_terms} must not go through it. Returns the updated row.
 */
export const updateGalleryCaptionTerms = (opts: {
  data: { id: string; captions_terms: string | null };
}) => fetchApi<any>("content", "updateGalleryCaptionTerms", opts.data);

export const deleteGalleryItem = (opts: { data: { id: string } }) =>
  fetchApi<{ ok: true }>("content", "deleteGalleryItem", opts.data);

export const reorderGalleryItems = (opts: {
  data: { items: Array<{ id: string; display_order: number }> };
}) => fetchApi<{ ok: true }>("content", "reorderGalleryItems", opts.data);
